/* eslint-disable security/detect-object-injection */
/* eslint-disable radix */
/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { INodeProps, Module, Node } from '@msdyn365-commerce-modules/utilities';
import React, { useEffect, useState } from 'react';

import { AddToCartComponent } from '@msdyn365-commerce/components';
import { AsyncResult, ProductSearchResult, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import IncrementalQuantity from '../../themes/master/views/components/incrementalQuantity';
import ProductPopover from '../../themes/master/views/components/product-popover.component';
import { IProductCollectionViewProps, IProductComponentViewProps } from './product-collection';
import { IProductCollectionResources } from './product-collection.props.autogenerated';

import {
    getProductAvailabilitiesForSelectedVariant,
    getSimpleProductsAction,
    ProductAvailabilitiesForSelectedVariantInput,
    ProductInput
} from '@msdyn365-commerce-modules/retail-actions';
import { toast, Toaster } from 'sonner';
import { Availability } from '../../actions/get-product-availabilities-for-selected-variant.action';
import { DEFAULT_MAX_QUANTITY } from '../../utilities/quantity-constants';
import { fetchVArticleMap } from '../../utilities/v-article-ids';

const _showToast = (heading: string, description: string) => {
    toast.error(
        <div className='toast' role='alert'>
            <div className='toast-header'>
                <h4>{heading}</h4>
            </div>
            <div className='toast-body'>
                <span>{description}</span>
            </div>
        </div>
    );
};

const ProductCollectionView: React.FC<IProductCollectionViewProps> = props => {
    const {
        heading,
        productsWithOutComponent,
        ProductCollectionContainer,
        products,
        SingleSlideCarouselComponentProps,
        GridComponentProps,
        isCarousel,
        isList,
        seeAllButton,
        resources
    } = props;

    if (products) {
        return (
            <Module {...ProductCollectionContainer}>
                {heading}
                {seeAllButton}
                {isList
                    ? _renderList(productsWithOutComponent, props)
                    : isCarousel
                    ? _renderCarousel(SingleSlideCarouselComponentProps, products)
                    : _renderGrid(GridComponentProps, products)}
            </Module>
        );
    }

    // If no products are retrieved and the list type is promotional, still render the module to keep the entry of promotion page
    if (props.config.productCollection.listType === 'promotional') {
        return (
            <Module {...ProductCollectionContainer}>
                {heading}
                {seeAllButton}
                {_renderNoProduct(resources)}
            </Module>
        );
    }
    props.context.telemetry.error('Product collection content is empty, module wont render.');
    return null;
};

interface errorMessage {
    LocalizedMessageParameters: string;
}

const _renderList = (items: ProductSearchResult[], props: React.PropsWithChildren<IProductCollectionViewProps>): JSX.Element => {
    const { context, typeName, id, resources } = props;
    const products = items;
    const [quantities, setQuantities] = useState<number[]>(new Array(items?.length).fill(1));
    const [availabilities, setAvailabilities] = useState(Array(products?.length).fill(Availability.loading));
    const [FullProducts, setFullProducts] = useState<SimpleProduct[]>([]);
    const [surfaceFinishingValues, setSurfaceFinishingValues] = useState<string[]>(new Array(items?.length).fill(''));
    const [surfaceFinishingDisabled, setSurfaceFinishingDisabled] = useState<boolean[]>(new Array(items?.length).fill(true));

    useEffect(() => {
        const fetchProducts = async () => {
            const simpleProducts = await _getProducts(products);
            setFullProducts(simpleProducts);

            simpleProducts.map((p: SimpleProduct, i) => {
                const minQ = p.Behavior?.MinimumQuantity ?? 0;
                const mulQ = p.Behavior?.MultipleOfQuantity ?? 0;
                handleQuantityChange(i, minQ > 0 ? minQ : mulQ > 0 ? mulQ : 1);
            });

            const ids = simpleProducts.map(p => String(p?.ItemId ?? '')).filter(Boolean);
            if (ids.length > 0) {
                try {
                    const map = await fetchVArticleMap(ids, context.actionContext);
                    const surfaceDisabledPerRow = items.map((_, i) => {
                        const id = String(simpleProducts[i]?.ItemId ?? '');
                        return id ? !map[id] : true;
                    });
                    setSurfaceFinishingDisabled(surfaceDisabledPerRow);
                } catch {
                    setSurfaceFinishingDisabled(items.map(() => true));
                }
            } else {
                setSurfaceFinishingDisabled(items.map(() => true));
            }
        };

        const fetchAvailabilities = async () => {
            for (let index = 0; index < products.length; index++) {
                const product = products[index];
                if (product) {
                    const availability = await newAvailableQuantity(product, context);
                    setAvailabilities(prevAvailabilities => {
                        const updatedAvailabilities = [...prevAvailabilities];
                        updatedAvailabilities[index] =
                            availability?.PhysicalAvailableInventoryLevelLabel === Availability.available
                                ? Availability.available
                                : Availability.outOfStock;
                        return updatedAvailabilities;
                    });
                }
            }
        };

        void fetchProducts();
        void fetchAvailabilities();
    }, [products, context]);

    const _getProducts = (products: ProductSearchResult[] = []): Promise<SimpleProduct[]> => {
        const productInputs = products
            .filter(product => product.RecordId)
            .map(product => new ProductInput(product.RecordId, props.context.request.apiSettings));
        return getSimpleProductsAction(productInputs, props.context.actionContext);
    };

    const newAvailableQuantity = async (product: any, context: any): Promise<AsyncResult<any> | null> => {
        try {
            const availability = await getProductAvailabilitiesForSelectedVariant(
                new ProductAvailabilitiesForSelectedVariantInput(
                    Number(product.RecordId),
                    Number(context.actionContext.requestContext.channel.RecordId),
                    product
                ),
                context.actionContext
            );
            return availability;
        } catch (error) {
            return null;
        }
    };

    const handleQuantityChange = (index: number, value: number) => {
        const newQuantities = [...quantities];
        // eslint-disable-next-line security/detect-object-injection
        newQuantities[index] = value < 1 ? 1 : value; // Ensure quantity is at least 1
        setQuantities(newQuantities);
    };

    const handleSurfaceFinishingChange = (index: number, value: string) => {
        const updateSurfaceValues = [...surfaceFinishingValues];
        updateSurfaceValues[index] = value;
        setSurfaceFinishingValues(updateSurfaceValues);
    };

    return (
        <>
            <Toaster
                richColors
                position='top-right'
                toastOptions={{
                    classNames: {
                        error: 'toast-danger'
                    }
                }}
            />
            <Node className={''}>
                <div className='table-responsive'>
                    <table className='ms-search-result-container__product-table table table-sm'>
                        {items.map((product: ProductSearchResult, index: number) => {
                            const simpleProduct: SimpleProduct = {
                                RecordId: product.RecordId,
                                Price: product.Price,
                                ProductTypeValue: 0,
                                BasePrice: product.BasePrice || 0,
                                AdjustedPrice: product.Price
                            };
                            const sfValue = surfaceFinishingValues[index] ?? '';
                            if (sfValue && sfValue.trim().length > 0) {
                                // // @ts-expect-error CommerceProperty typing
                                simpleProduct.ExtensionProperties = [
                                    {
                                        Key: 'SurfaceFinishing',
                                        Value: { StringValue: sfValue }
                                    }
                                ];
                            } else {
                                simpleProduct.ExtensionProperties = [];
                            }
                            return (
                                <tr key={index}>
                                    <td>
                                        <ProductPopover product={product} index={index} context={context} layout='imagelist' />
                                    </td>
                                    <td>
                                        {!surfaceFinishingDisabled[index] && (
                                            <input
                                                className='ms-search-result__surface-finishing'
                                                type='text'
                                                disabled={surfaceFinishingDisabled[index]}
                                                placeholder={resources.addSurfaceFinishingText}
                                                value={surfaceFinishingValues[index] ?? ''}
                                                onChange={e => handleSurfaceFinishingChange(index, e.currentTarget.value)}
                                            />
                                        )}
                                    </td>
                                    <td>
                                        {FullProducts.length > 0 && (
                                            <IncrementalQuantity
                                                id='ms-buybox__product-quantity-input'
                                                min={1}
                                                max={(() => {
                                                    const behavior = FullProducts.find(
                                                        productResult => product.RecordId === productResult.RecordId
                                                    )?.Behavior;
                                                    return behavior?.MaximumQuantity && behavior.MaximumQuantity > 0
                                                        ? behavior.MaximumQuantity
                                                        : DEFAULT_MAX_QUANTITY;
                                                })()}
                                                currentCount={quantities[index]}
                                                onChange={value => {
                                                    handleQuantityChange(index, value);
                                                    return true;
                                                }}
                                                productBehavior={
                                                    FullProducts.find(productResult => product.RecordId === productResult.RecordId)
                                                        ?.Behavior
                                                }
                                            />
                                        )}
                                    </td>
                                    <td>
                                        {availabilities[index] === Availability.loading ? (
                                            <span className='ms-search-result-container__loading'></span>
                                        ) : (
                                            <span
                                                className={`ms-search-result-container__availability-${
                                                    availabilities[index] === Availability.available
                                                        ? Availability.availableClass
                                                        : Availability.outOfStockClass
                                                }`}
                                                title={
                                                    availabilities[index] === Availability.available
                                                        ? resources.availabilityIndicatorAvailable
                                                        : resources.availabilityIndicatorOutOfStock
                                                }
                                            ></span>
                                        )}
                                    </td>
                                    <td>
                                        <AddToCartComponent
                                            quantity={quantities[index]}
                                            id={id}
                                            context={context}
                                            typeName={typeName}
                                            addToCartText={resources.addToCartText}
                                            data={{ product: simpleProduct }}
                                            disabled={false}
                                            onError={error => {
                                                const obj = error.cartActionResult?.validationResults?.find(
                                                    x =>
                                                        x.ErrorResourceId ===
                                                        'Microsoft_Dynamics_Commerce_Runtime_ItemQuantityViolatesMultiplicityOfConfiguredNumber'
                                                );
                                                const object: errorMessage = obj as errorMessage;
                                                const param = object.LocalizedMessageParameters[0];

                                                if (param !== undefined) {
                                                    _showToast(
                                                        product.Name!,
                                                        props.resources.errorMessageForQtyValidation.replace('{param}', param)
                                                    );
                                                }
                                            }}
                                        ></AddToCartComponent>
                                    </td>
                                </tr>
                            );
                        })}
                    </table>
                </div>
            </Node>
        </>
    );
};

const _renderCarousel = (carouselContainer: INodeProps, items: IProductComponentViewProps[]): JSX.Element => {
    return <Node className={''}>{items && items.map(_renderProduct)}</Node>;
};

const _renderGrid = (gridContainer: INodeProps, items: IProductComponentViewProps[]): JSX.Element => {
    return <Node {...gridContainer}>{items && items.map(_renderProduct)}</Node>;
};

const _renderNoProduct = (resources: IProductCollectionResources): JSX.Element => {
    return (
        <Node className={''}>
            <p>{resources.noProductToRenderText}</p>
        </Node>
    );
};

const _renderProduct = (product: IProductComponentViewProps): JSX.Element => {
    const { ProductContainer, productComponent } = product;

    return <Node {...ProductContainer}>{productComponent}</Node>;
};

export default ProductCollectionView;
